/**
 * 반복 규칙 — 클라이언트 (spec docs/superpowers/specs/2026-09-18-calendar-recurrence-design.md §3.2·§6.1).
 *
 * 패널의 「반복」 행: <select id="calRepeat"> (없음·매일·매주·매월·매년·사용자 지정…) +
 * 사용자 지정 판 #calRepeatCustom + 숨은 repeat_json. 인스턴스를 열면 잠긴 상태
 * (요약 + 「규칙 바꾸기…」)이고, 풀어야 repeat_json 이 채워진다. 요약 문구는 서버의
 * SeriesRule.Summary 와 같은 규칙이며 라벨은 행의 data-* 에서 온다.
 */

import { syncSelect } from './inapp-select.js';
import { attachDatePicker, syncDateLabel } from './datepicker.js';

const WD_KO = ['일', '월', '화', '수', '목', '금', '토'];

function parseDate(d) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d || '');
    return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])) : null;
}
function fmtN(tpl, ...args) {
    let i = 0;
    return String(tpl || '').replace(/%[sd]/g, () => (i < args.length ? String(args[i++]) : ''));
}
function csv(s, fallback) {
    const v = String(s || '').split(',').map((x) => x.trim()).filter(Boolean);
    return v.length ? v : fallback;
}

/** ruleFromPreset('weekly', '2026-09-14') → {freq:'weekly', interval:1, by_weekday:[1], end:{kind:'never'}} */
export function ruleFromPreset(kind, dtstart) {
    const d = parseDate(dtstart);
    if (kind === 'weekdays') return { freq: 'weekly', interval: 1, by_weekday: [1, 2, 3, 4, 5], end: { kind: 'never' } }; // 평일만 (BA5 c)
    const rule = { freq: kind, interval: 1, end: { kind: 'never' } };
    if (kind === 'weekly') rule.by_weekday = [d ? d.getUTCDay() : 1];
    if (kind === 'monthly') rule.monthly = { kind: 'day' };
    return rule;
}

/** summarize(rule, dtstart, L) — L = data-* of the repeat row (sumDaily, wdShort, …). */
export function summarize(rule, dtstart, L) {
    if (!rule || !rule.freq) return '';
    L = L || {};
    const d = parseDate(dtstart) || new Date();
    const iv = Math.max(1, rule.interval | 0);
    const wdShort = csv(L.wdShort, WD_KO);
    const wdLong = csv(L.wdLong, WD_KO.map((w) => w + '요일'));
    const nths = csv(L.nthCsv, ['첫째', '둘째', '셋째', '넷째']);
    let head = '';
    switch (rule.freq) {
    case 'daily':
        head = iv === 1 ? (L.sumDaily || '매일') : fmtN(L.sumEveryDays || '%d일마다', iv);
        break;
    case 'weekly': {
        const days = (rule.by_weekday && rule.by_weekday.length ? rule.by_weekday : [d.getUTCDay()]).slice().sort((a, b) => a - b);
        const list = days.map((x) => wdShort[x] || '').join('·');
        if (iv === 1 && days.join(',') === '1,2,3,4,5') head = L.sumWeekdays || '평일마다';
        else head = iv === 1 ? fmtN(L.sumWeekly || '매주 %s', list) : fmtN(L.sumEveryWeeks || '%d주마다 %s', iv, list);
        break;
    }
    case 'monthly': {
        const m = rule.monthly || { kind: 'day' };
        if (m.kind === 'nth') {
            const nth = m.nth === -1 ? (L.nthLast || '마지막') : (nths[(m.nth | 0) - 1] || '');
            const wd = wdLong[m.weekday | 0] || '';
            head = iv === 1 ? fmtN(L.sumMonthlyNth || '매월 %s %s', nth, wd) : fmtN(L.sumEveryMonthsNth || '%d개월마다 %s %s', iv, nth, wd);
        } else {
            head = iv === 1 ? fmtN(L.sumMonthlyDay || '매월 %d일', d.getUTCDate()) : fmtN(L.sumEveryMonthsDay || '%d개월마다 %d일', iv, d.getUTCDate());
        }
        break;
    }
    case 'yearly':
        head = iv === 1 ? fmtN(L.sumYearly || '매년 %d월 %d일', d.getUTCMonth() + 1, d.getUTCDate())
            : fmtN(L.sumEveryYears || '%d년마다 %d월 %d일', iv, d.getUTCMonth() + 1, d.getUTCDate());
        break;
    default:
        return '';
    }
    const end = rule.end || { kind: 'never' };
    if (end.kind === 'count') head += fmtN(L.sumCount || ' · %d회', end.count | 0);
    else if (end.kind === 'until' && end.until) head += fmtN(L.sumUntil || ' · %s까지', end.until);
    if (rule.skip_holidays) head += L.sumSkipHolidays || ' · 공휴일 제외';
    return head;
}

/**
 * bindRepeatControl(row) — row = .cal-prop[data-field="repeat"]. Returns
 *   reset()                      새 일정: 없음
 *   setDtstart(date)             시작일이 바뀌면 프리셋 규칙을 다시 만든다
 *   lock(rule, dtstart)          인스턴스: 요약 + 「규칙 바꾸기…」, repeat_json 비움
 *   unlock()                     규칙 편집 시작 (repeat_json 채우기 시작)
 *   getRule() / isChanged()      현재 규칙 · 인스턴스에서 규칙을 바꿨는가
 *   summaryOf(rule, dtstart)     문구
 */
export function bindRepeatControl(row) {
    if (!row || row.dataset.bound) return null;
    row.dataset.bound = '1';
    const L = row.dataset;
    const sel = row.querySelector('#calRepeat');
    const json = row.querySelector('#calRepeatJson');
    const summary = row.querySelector('#calRepeatSummary');
    const changeBtn = row.querySelector('#calRepeatChange');
    const custom = row.querySelector('#calRepeatCustom');
    const interval = row.querySelector('#calRepInterval');
    const unit = row.querySelector('#calRepUnit');
    const wdBox = row.querySelector('#calRepWeekdays');
    const monthly = row.querySelector('#calRepMonthly');
    const dayLbl = row.querySelector('#calRepMonthDayLbl');
    const nthLbl = row.querySelector('#calRepMonthNthLbl');
    const endSel = row.querySelector('#calRepEnd');
    const countIn = row.querySelector('#calRepCount');
    const untilIn = row.querySelector('#calRepUntil');
    const skipHol = row.querySelector('#calRepSkipHolidays');
    if (!sel || !json) return null;
    const untilWrap = row.querySelector('#calRepUntilWrap');
    const untilBtn = row.querySelector('#calRepUntilBtn');
    const modal = row.closest('#calendarModal');
    const pickPh = modal?.dataset.pickDate || '날짜 선택';
    if (untilBtn && untilIn) attachDatePicker(untilBtn, untilIn, {
        weekdays: modal?.dataset.weekdays, placeholder: pickPh,
        onSet: () => untilIn.dispatchEvent(new Event('change', { bubbles: true })),
    });
    function showPreset(show) {
        (sel.closest('.cal-sel') || sel).hidden = !show;
    }

    let rule = null;
    let dtstart = '';
    let locked = false;
    let changed = false;

    function nthOf(d) {
        const day = d.getUTCDate();
        return { nth: Math.floor((day - 1) / 7) + 1, weekday: d.getUTCDay() };
    }
    function paintPresetLabels() {
        const d = parseDate(dtstart) || new Date();
        const wdLong = csv(L.wdLong, WD_KO.map((w) => w + '요일'));
        const nths = csv(L.nthCsv, ['첫째', '둘째', '셋째', '넷째']);
        if (dayLbl) dayLbl.textContent = fmtN(L.onDay || '%d일', d.getUTCDate());
        if (nthLbl) {
            const n = nthOf(d);
            nthLbl.textContent = fmtN(L.onNth || '%s %s', nths[n.nth - 1] || '', wdLong[n.weekday] || '');
        }
        const opt = (v) => sel.querySelector('option[value="' + v + '"]');
        const w = opt('weekly');
        if (w) w.textContent = summarize(ruleFromPreset('weekly', dtstart), dtstart, L);
        const m = opt('monthly');
        if (m) m.textContent = summarize(ruleFromPreset('monthly', dtstart), dtstart, L);
        const y = opt('yearly');
        if (y) y.textContent = summarize(ruleFromPreset('yearly', dtstart), dtstart, L);
    }
    function writeJSON() {
        const next = rule && (!locked || changed) ? JSON.stringify(rule) : '';
        if (json.value !== next) {
            json.value = next;
            json.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (summary) { // select 가 보일 땐 그 라벨이 요약이라 따로 보이지 않는다(잠긴 인스턴스만)
            summary.textContent = rule ? summarize(rule, dtstart, L) : '';
            summary.hidden = !rule || !locked;
        }
        const customOpt = sel.querySelector('option[value="custom"]');
        if (customOpt && rule && sel.value === 'custom') customOpt.textContent = summarize(rule, dtstart, L);
        else if (customOpt) customOpt.textContent = L.customLabel || '사용자 지정…';
        [sel, unit, endSel].forEach(syncSelect);
        if (untilBtn) syncDateLabel(untilBtn, untilIn, pickPh);
    }
    function panelFromRule() {
        const r = rule || ruleFromPreset('weekly', dtstart);
        if (interval) interval.value = String(Math.max(1, r.interval | 0));
        if (unit) unit.value = r.freq;
        if (wdBox) {
            const days = r.by_weekday || [];
            wdBox.querySelectorAll('input').forEach((cb) => { cb.checked = days.indexOf(+cb.value) >= 0; });
        }
        if (monthly) {
            const kind = r.monthly && r.monthly.kind === 'nth' ? 'nth' : 'day';
            const radio = monthly.querySelector('input[value="' + kind + '"]');
            if (radio) radio.checked = true;
        }
        const end = r.end || { kind: 'never' };
        if (endSel) endSel.value = end.kind || 'never';
        if (countIn) countIn.value = String(end.count || 10);
        if (untilIn) untilIn.value = end.until || '';
        if (skipHol) skipHol.checked = !!r.skip_holidays;
        paintPanelVisibility();
    }
    function paintPanelVisibility() {
        const freq = unit ? unit.value : 'weekly';
        if (wdBox) wdBox.hidden = freq !== 'weekly';
        if (monthly) monthly.hidden = freq !== 'monthly';
        const kind = endSel ? endSel.value : 'never';
        if (countIn) countIn.hidden = kind !== 'count';
        if (untilWrap) untilWrap.hidden = kind !== 'until';
    }
    function ruleFromPanel() {
        const r = { freq: unit ? unit.value : 'weekly', interval: Math.min(99, Math.max(1, parseInt(interval && interval.value, 10) || 1)), end: { kind: 'never' } };
        if (r.freq === 'weekly' && wdBox) {
            const days = Array.from(wdBox.querySelectorAll('input:checked')).map((cb) => +cb.value);
            r.by_weekday = days.length ? days : [(parseDate(dtstart) || new Date()).getUTCDay()];
        }
        if (r.freq === 'monthly') {
            const kind = monthly && monthly.querySelector('input[value="nth"]:checked') ? 'nth' : 'day';
            if (kind === 'nth') {
                const n = nthOf(parseDate(dtstart) || new Date());
                r.monthly = { kind: 'nth', nth: n.nth, weekday: n.weekday };
            } else r.monthly = { kind: 'day' };
        }
        const kind = endSel ? endSel.value : 'never';
        if (kind === 'count') r.end = { kind: 'count', count: Math.min(730, Math.max(1, parseInt(countIn && countIn.value, 10) || 1)) };
        else if (kind === 'until' && untilIn && untilIn.value) r.end = { kind: 'until', until: untilIn.value };
        if (skipHol && skipHol.checked) r.skip_holidays = true; // 공휴일 건너뛰기 (BA5 c) — 규칙 JSON 에만, 마이그레이션 없음
        return r;
    }
    function showCustom(on) {
        if (custom) custom.hidden = !on;
    }

    sel.addEventListener('change', () => {
        changed = true;
        const v = sel.value;
        if (v === 'none') { rule = null; showCustom(false); }
        else if (v === 'custom') { showCustom(true); panelFromRule(); rule = ruleFromPanel(); }
        else { rule = ruleFromPreset(v, dtstart); showCustom(false); }
        writeJSON();
    });
    if (custom) {
        custom.addEventListener('change', () => { paintPanelVisibility(); rule = ruleFromPanel(); changed = true; writeJSON(); });
        custom.addEventListener('input', () => { rule = ruleFromPanel(); changed = true; writeJSON(); });
    }
    if (changeBtn) {
        changeBtn.addEventListener('click', () => api.unlock());
    }

    const api = {
        reset() {
            rule = null; locked = false; changed = false;
            sel.value = 'none'; showPreset(true);
            if (changeBtn) changeBtn.hidden = true;
            showCustom(false);
            writeJSON();
        },
        setDtstart(d) {
            dtstart = d || '';
            paintPresetLabels();
            if (rule && sel.value !== 'custom' && sel.value !== 'none' && !locked) rule = ruleFromPreset(sel.value, dtstart);
            else if (rule && sel.value === 'custom' && !locked) rule = ruleFromPanel();
            writeJSON();
        },
        lock(r, d) {
            rule = r || null; dtstart = d || dtstart; locked = true; changed = false;
            paintPresetLabels();
            showPreset(false);
            showCustom(false);
            if (changeBtn) changeBtn.hidden = !rule;
            json.value = '';
            if (summary) { summary.textContent = rule ? summarize(rule, dtstart, L) : ''; summary.hidden = !rule; }
        },
        unlock() {
            locked = false; changed = true;
            showPreset(true);
            sel.value = 'custom';
            showCustom(true);
            panelFromRule();
            rule = ruleFromPanel();
            if (changeBtn) changeBtn.hidden = true;
            writeJSON();
        },
        // setRule: 바깥(제목 감지 AZ4)이 정한 규칙을 사용자 지정 판으로 보인다 — 새 일정에서만
        setRule(r, d) {
            if (locked || !r) return;
            if (d) { dtstart = d; paintPresetLabels(); }
            changed = true;
            sel.value = 'custom';
            showCustom(true);
            rule = Object.assign({ end: { kind: 'never' } }, r);
            panelFromRule();
            rule = ruleFromPanel();
            writeJSON();
        },
        getRule: () => rule,
        isLocked: () => locked,
        isChanged: () => changed && !!json.value,
        summaryOf: (r, d) => summarize(r, d, L),
    };
    paintPanelVisibility();
    return api;
}
